import Vue from 'vue'

/*
 * Le portail terrain. En GET, les paramètres passent par `data` : auth.fetch ne recopie pas `params`.
 */
class FieldRepository {

  // GET /api/field/orders - { mine, open, notReady } ; avec une recherche (adresse, nom, n° de commande), toutes les
  // poses ouvertes du périmètre qui y répondent
  orders(search = null) {
    return this.fetch({ url: '/api/field/orders', method: 'get', data: search ? { search } : {} })
  }

  // GET /api/field/orders/{id}
  // GET /api/field/supervision — les supervisions ZEborne à joindre avant d'avoir une pose
  supervision() {
    return this.fetch({ url: '/api/field/supervision', method: 'get' })
  }

  // GET /api/field/orders/{id}/locations — les emplacements de l'organisation, au choix sur place
  locations(id) {
    return this.fetch({ url: `/api/field/orders/${id}/locations`, method: 'get' })
  }

  order(id) {
    return this.fetch({ url: `/api/field/orders/${id}`, method: 'get' })
  }

  // POST /api/field/orders/{id}/take
  take(id) {
    return this.fetch({ url: `/api/field/orders/${id}/take`, method: 'post' })
  }

  // GET /api/field/orders/{id}/activation-preview — quel équipement, et le plan
  // onSite : { identifier } ou { meterDeviceId }, et newLocation[...] pour un site sans emplacement
  activationPreview(id, onSite) {
    return this.fetch({ url: `/api/field/orders/${id}/activation-preview`, method: 'get', data: onSite })
  }

  // POST /api/field/orders/{id}/activation — poser (prend la demande si elle était libre)
  activate(id, onSite) {
    return this.fetch({ url: `/api/field/orders/${id}/activation`, method: 'post', data: onSite })
  }

  // POST /api/field/orders/{id}/release — rendre la pose : elle redevient libre, le rendez-vous reste
  release(id) {
    return this.fetch({ url: `/api/field/orders/${id}/release`, method: 'post' })
  }

  // POST /api/field/orders/{id}/issue — { reason: person-absent|site-not-compliant|wrong-equipment|other, note }
  // la pose redevient libre et « à planifier » pour le bureau
  reportIssue(id, reason, note) {
    return this.fetch({ url: `/api/field/orders/${id}/issue`, method: 'post', data: { reason, note } })
  }

  // GET /api/field/charging-points - { chargingPoints } : ?search=… (identifiant, n° de série, adresse, client) ou ?mine=1
  chargingPoints({ search = null, mine = false } = {}) {
    return this.fetch({ url: '/api/field/charging-points', method: 'get', data: mine ? { mine: 1 } : { search } })
  }

  // GET /api/field/charging-points/{id} - la fiche de maintenance
  chargingPoint(id) {
    return this.fetch({ url: `/api/field/charging-points/${id}`, method: 'get' })
  }

  // GET /api/field/charging-points/{id}/configuration - la dernière configuration lue
  configuration(id) {
    return this.fetch({ url: `/api/field/charging-points/${id}/configuration`, method: 'get' })
  }

  // POST /api/field/charging-points/{id}/actions/{action} - { status, reason, messageIds }
  // action : reset { type }, unlock-connector { evseId, connectorId }, change-availability { type, connectorId },
  // trigger-message { messageType, connectorId }, get-configuration
  sendAction(id, action, params = {}) {
    return this.fetch({ url: `/api/field/charging-points/${id}/actions/${action}`, method: 'post', data: params })
  }

  // GET /api/field/charging-points/{id}/actions/{messageId} - { state: pending|answered|error, response, error }
  actionOutcome(id, messageId) {
    return this.fetch({ url: `/api/field/charging-points/${id}/actions/${messageId}`, method: 'get' })
  }

  // GET /api/field/charging-points/{id}/test-charge?evseId=&since= - { session: { status, energyWh, durationSeconds… } | null }
  testCharge(id, evseId, since) {
    return this.fetch({ url: `/api/field/charging-points/${id}/test-charge`, method: 'get', data: { evseId, since } })
  }

  // GET /api/field/charging-points/{id}/sessions - { sessions: [{ status, energyWh, badge, cdr: { state, errors } | null }] }
  sessions(id) {
    return this.fetch({ url: `/api/field/charging-points/${id}/sessions`, method: 'get' })
  }

  // GET /api/field/charging-points/{id}/sessions/{sessionId} - { startTime, stopTime, metrics, events }
  sessionCurve(id, sessionId) {
    return this.fetch({ url: `/api/field/charging-points/${id}/sessions/${sessionId}`, method: 'get' })
  }

  // GET /api/field/charging-points/{id}/badge-test - sans since : { since } (heure du serveur) ; avec : { badges }
  badgeTest(id, since = null) {
    return this.fetch({ url: `/api/field/charging-points/${id}/badge-test`, method: 'get', data: since ? { since } : {} })
  }

  // GET /api/field/charging-points/{id}/intervention - { intervention: { id, kind, open, testMode, testModeUntil… } | null }
  intervention(id) {
    return this.fetch({ url: `/api/field/charging-points/${id}/intervention`, method: 'get' })
  }

  // POST /api/field/charging-points/{id}/intervention - { orderId } pour une pose : l'intervention ouverte
  openIntervention(id, orderId = null) {
    return this.fetch({ url: `/api/field/charging-points/${id}/intervention`, method: 'post', data: orderId ? { orderId } : {} })
  }

  // POST /api/field/interventions/{id}/{change} - change : activate-test-mode | close
  changeIntervention(interventionId, change) {
    return this.fetch({ url: `/api/field/interventions/${interventionId}/${change}`, method: 'post' })
  }

  // GET /api/field/interventions/{id}/members?searchText - { items: [{ id, name, organization }] } : les membres à simuler
  testMembers(interventionId, searchText = '') {
    return this.fetch({ url: `/api/field/interventions/${interventionId}/members`, method: 'get', data: searchText ? { searchText } : {} })
  }

  // POST /api/field/interventions/{id}/simulate - { memberId, badgeUid? } : le membre simulé et son badge de test
  simulateMember(interventionId, memberId, badgeUid = null) {
    return this.fetch({ url: `/api/field/interventions/${interventionId}/simulate`, method: 'post', data: badgeUid ? { memberId, badgeUid } : { memberId } })
  }

  // POST /api/field/interventions/{id}/test-charge - { evseId } : la charge de test à distance, au nom du membre simulé
  startTestCharge(interventionId, evseId) {
    return this.fetch({ url: `/api/field/interventions/${interventionId}/test-charge`, method: 'post', data: { evseId } })
  }

  // POST /api/field/interventions/{id}/associate-badge - { uid, memberId, corporate, confirmExternal } : badge associé sur place
  associateBadge(interventionId, data) {
    return this.fetch({ url: `/api/field/interventions/${interventionId}/associate-badge`, method: 'post', data })
  }

  fetch(options) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch(options)
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }
}

export default new FieldRepository()
